import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Star, CheckCircle, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

interface DestinationPageProps {
  name: string;               // "Egipt"
  nameRo: string;             // "Egipt" (for display)
  slug: string;               // "egipt"
  heroImage: string;
  flag: string;
  metaTitle: string;
  metaDescription: string;
  tagline: string;
  description: string;
  highlights: string[];
  included: string[];
  faqs: { q: string; a: string }[];
  relatedDestinations: { name: string; slug: string; image: string }[];
}

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function DestinationPage({
  name, heroImage, flag, metaTitle, metaDescription,
  tagline, description, highlights, included, faqs, relatedDestinations,
}: DestinationPageProps) {

  // ── Actualizare titlu & meta per pagină (important SEO) ──────────────────
  useEffect(() => {
    document.title = metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", metaDescription);
    window.scrollTo(0, 0);
  }, [metaTitle, metaDescription]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">

      {/* ─── HEADER ─── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b"
        style={{ background: "rgba(13,31,45,0.96)", borderColor: "rgba(191,155,87,0.25)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src="/logo.png" alt="Premium Style Travel" className="h-12 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/80 hover:text-white text-sm uppercase tracking-widest transition-colors">
              Acasă
            </Link>
            <Link to="/#destinatii" onClick={() => setTimeout(() => scrollTo("destinatii"), 100)}
              className="text-white/80 hover:text-white text-sm uppercase tracking-widest transition-colors">
              Destinații
            </Link>
            <button onClick={() => { window.location.href = "/#formular"; }}
              className="px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-widest transition-all"
              style={{ background: "#BF9B57", color: "#fff" }}>
              Solicită Ofertă
            </button>
          </nav>
          <a href="tel:+37360609292" className="flex items-center gap-2 text-sm font-medium" style={{ color: "#BF9B57" }}>
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline font-mono">+373 60609292</span>
          </a>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${heroImage}')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f2d]/70 via-[#0d1f2d]/50 to-[#0d1f2d]/90" />
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#BF9B57] to-transparent" />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-5"
            style={{ background: "rgba(191,155,87,0.15)", border: "1px solid rgba(191,155,87,0.35)" }}>
            <span className="text-xl">{flag}</span>
            <span className="text-xs uppercase tracking-widest font-medium" style={{ color: "#BF9B57" }}>
              Premium Style Travel
            </span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: "var(--font-heading)" }}>
            Vacanță în <span style={{ color: "#BF9B57" }}>{name}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/75 text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
            {tagline}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => { window.location.href = "/#formular"; }}
              className="px-8 py-3.5 rounded-full font-semibold text-sm uppercase tracking-widest transition-all"
              style={{ background: "#BF9B57", color: "#fff", boxShadow: "0 4px 20px -4px rgba(191,155,87,0.5)" }}>
              Solicită Ofertă Gratuită
            </button>
            <a href="tel:+37360609292"
              className="px-8 py-3.5 rounded-full font-semibold text-sm uppercase tracking-widest transition-all border flex items-center justify-center gap-2"
              style={{ background: "rgba(35,75,110,0.4)", color: "#fff", borderColor: "rgba(35,75,110,0.8)" }}>
              <Phone className="w-4 h-4" /> Sună Acum
            </a>
          </motion.div>
        </div>
      </section>

      {/* ─── DESCRIPTION ─── */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: "#BF9B57" }}>
                De ce {name}?
              </p>
              <h2 className="text-3xl font-bold text-foreground mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                Descoperă frumusețile destinației
              </h2>
              <p className="text-muted-foreground leading-relaxed text-sm">{description}</p>
            </div>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest mb-4 font-medium" style={{ color: "#BF9B57" }}>
                Ce include pachetul
              </p>
              {included.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                  <CheckCircle className="w-5 h-5 shrink-0" style={{ color: "#BF9B57" }} />
                  <span className="text-sm text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── HIGHLIGHTS ─── */}
      <section className="py-20 px-4" style={{ background: "#0d1f2d" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: "#BF9B57" }}>
              Atracții principale
            </p>
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "var(--font-heading)" }}>
              Ce veți vedea în {name}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {highlights.map((h, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="flex items-start gap-3 p-5 rounded-2xl border border-white/10 hover:border-[#BF9B57]/40 transition-colors">
                <Star className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#BF9B57" }} />
                <span className="text-white/80 text-sm">{h}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: "#BF9B57" }}>
              Întrebări frecvente
            </p>
            <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
              Vacanță în {name} — FAQ
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-5 rounded-2xl border border-border bg-card">
                <h3 className="font-semibold text-foreground mb-2 text-sm">{faq.q}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-20 px-4" style={{ background: "#0d1f2d" }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            Gata să plecați în <span style={{ color: "#BF9B57" }}>{name}</span>?
          </h2>
          <p className="text-white/60 mb-8">Completați formularul sau sunați-ne și vă pregătim oferta personalizată în 24 ore.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { window.location.href = "/#formular"; }}
              className="px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-widest"
              style={{ background: "#BF9B57", color: "#fff", boxShadow: "0 4px 20px -4px rgba(191,155,87,0.5)" }}>
              Solicită Oferta →
            </button>
            <a href="tel:+37360609292"
              className="px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-widest border flex items-center justify-center gap-2"
              style={{ background: "rgba(35,75,110,0.4)", color: "#fff", borderColor: "rgba(35,75,110,0.8)" }}>
              <Phone className="w-4 h-4" /> +373 60609292
            </a>
          </div>
        </div>
      </section>

      {/* ─── RELATED ─── */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
            Alte destinații populare
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {relatedDestinations.map((d, i) => (
              <Link key={i} to={`/${d.slug}`}
                className="group relative h-48 rounded-2xl overflow-hidden border border-border hover:border-[#BF9B57]/50 transition-all">
                <img src={d.image} alt={`Vacanță ${d.name}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                  <span className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{d.name}</span>
                  <ChevronRight className="w-4 h-4" style={{ color: "#BF9B57" }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-10 px-4 border-t border-white/10 text-center" style={{ background: "#080f18" }}>
        <div className="flex items-center justify-center gap-2 mb-4">
          <img src="/logo.png" alt="Premium Style Travel" className="h-14 w-auto object-contain opacity-90" />
        </div>
        <div className="flex items-center justify-center gap-4 mb-3 flex-wrap">
          <a href="tel:+37360609292" className="text-white/60 text-xs font-mono hover:text-white transition-colors">+373 60609292</a>
          <span className="text-white/20">|</span>
          <a href="mailto:avitur88@gmail.com" className="text-white/60 text-xs hover:text-white transition-colors">avitur88@gmail.com</a>
          <span className="text-white/20">|</span>
          <span className="text-white/60 text-xs flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Str. Călărași 45, Chișinău
          </span>
        </div>
        <p className="text-white/25 text-xs">© {new Date().getFullYear()} Premium Style Travel. Toate drepturile rezervate.</p>
      </footer>
    </div>
  );
}
